import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardCopy, KeyRound, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";

import { ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "../account/components";
import { ExternalProviderConfiguration, api, formatDate } from "../api";

export function ExternalProviderSettingsPage() {
  const providers = useQuery({
    queryKey: ["operator-external-provider-settings"],
    queryFn: () => api<{ providers: ExternalProviderConfiguration[] }>("/api/v1/operator/super-admin/external-providers"),
  });

  return <>
    <PageHeader eyebrow="SUPER ADMIN" title="登录提供商" description="配置 Google、GitHub 和 Apple 登录。Secret 与 Apple 私钥加密保存，变更立即生效。" action={<StatusBadge tone="good"><KeyRound />加密凭据</StatusBadge>} />
    {providers.isPending && <LoadingState label="正在读取提供商配置" />}
    {providers.error && <ErrorState error={providers.error} retry={() => void providers.refetch()} />}
    {providers.data?.providers.map((provider) => <ProviderEditor key={`${provider.id}-${provider.version}-${provider.source}`} provider={provider} />)}
  </>;
}

function ProviderEditor({ provider }: { provider: ExternalProviderConfiguration }) {
  const client = useQueryClient();
  const [form, setForm] = useState({
    enabled: provider.enabled,
    clientId: provider.clientId,
    clientSecret: "",
    teamId: provider.teamId ?? "",
    keyId: provider.keyId ?? "",
    privateKeyPem: "",
  });
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setForm({ enabled: provider.enabled, clientId: provider.clientId, clientSecret: "", teamId: provider.teamId ?? "", keyId: provider.keyId ?? "", privateKeyPem: "" });
  }, [provider]);
  const refresh = () => client.invalidateQueries({ queryKey: ["operator-external-provider-settings"] });
  const save = useMutation({
    mutationFn: () => api<ExternalProviderConfiguration>(`/api/v1/operator/super-admin/external-providers/${provider.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...form, expectedVersion: provider.version }),
    }),
    onSuccess: refresh,
  });
  const reset = useMutation({
    mutationFn: () => api<ExternalProviderConfiguration>(`/api/v1/operator/super-admin/external-providers/${provider.id}/environment-reset`, {
      method: "POST",
      body: JSON.stringify({ expectedVersion: provider.version }),
    }),
    onSuccess: refresh,
  });
  const apple = provider.id === "apple";
  const credentialReady = apple ? provider.privateKeyConfigured : provider.clientSecretConfigured;

  return <Section title={provider.displayName} description={`当前来源：${provider.source === "DATABASE" ? "后台加密配置" : ".env 环境配置"} · 最近更新 ${formatDate(provider.updatedAt)}`} action={<StatusBadge tone={provider.enabled ? "good" : "neutral"}>{provider.enabled ? "已启用" : "未启用"}</StatusBadge>}>
    <div className="provider-callback"><div><span>授权回调 URL</span><code>{provider.redirectUri}</code></div><button className="icon-button" type="button" title="复制回调 URL" aria-label={`复制 ${provider.displayName} 回调 URL`} onClick={() => { void navigator.clipboard.writeText(provider.redirectUri); setCopied(true); }}><ClipboardCopy /></button>{copied && <small><Check />已复制</small>}</div>
    <form className="settings-form provider-settings-form" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
      <label className="check-row"><input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} /><span>允许使用 {provider.displayName} 登录和连接</span></label>
      <label><span>Client ID</span><input required maxLength={512} value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })} /></label>
      {apple ? <>
        <div className="field-grid"><label><span>Team ID</span><input required minLength={10} maxLength={10} value={form.teamId} onChange={(event) => setForm({ ...form, teamId: event.target.value })} /></label><label><span>Key ID</span><input required minLength={10} maxLength={10} value={form.keyId} onChange={(event) => setForm({ ...form, keyId: event.target.value })} /></label></div>
        <label><span>PKCS#8 P-256 私钥</span><textarea rows={5} maxLength={16384} value={form.privateKeyPem} onChange={(event) => setForm({ ...form, privateKeyPem: event.target.value })} placeholder={provider.privateKeyConfigured ? "已配置；留空保留当前私钥" : "-----BEGIN PRIVATE KEY-----"} /></label>
      </> : <label><span>Client Secret</span><input type="password" maxLength={4096} autoComplete="new-password" value={form.clientSecret} onChange={(event) => setForm({ ...form, clientSecret: event.target.value })} placeholder={provider.clientSecretConfigured ? "已配置；留空保留当前 Secret" : "尚未配置"} /></label>}
      <div className="provider-credential-state"><StatusBadge tone={credentialReady ? "good" : "warning"}>{credentialReady ? "凭据已配置" : "缺少凭据"}</StatusBadge><div className="form-actions"><button className="primary-button" disabled={save.isPending} type="submit"><Save />保存</button>{provider.source === "DATABASE" && <button className="secondary-button" disabled={reset.isPending} type="button" onClick={() => reset.mutate()}><RotateCcw />恢复 .env 配置</button>}</div></div>
      <MutationMessage error={save.error ?? reset.error} success={(save.isSuccess && "提供商配置已生效") || (reset.isSuccess && "已恢复环境配置")} />
    </form>
  </Section>;
}
